import { isArray } from '@ember/array';
import { isEmpty } from '@ember/utils';
import { conformJurisdictionalId } from 'linced-app/utils/fields';
import { validateCoreDetails } from 'linced-app/utils/nz-moe/common';
import DepartureReason from 'linced-app/enums/nz-moe-departure-reason';
import Area from 'linced-app/enums/nz-moe-area';
import PostSchoolActivity from 'linced-app/enums/nz-moe-post-school-activity';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import School from '@linc-technologies/linced-common/enums/nz-moe-school';
import moment from 'moment';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export function validateLeaver(person, enrolment, schoolType) {
  let errors = validateCoreDetails(person, enrolment, schoolType);

  if (isEmpty(enrolment.departureReason)) {
    errors.push('Reason for Leaving is not set');
  } else if (
    isEmpty(findBy(DepartureReason.values, 'value', enrolment.departureReason))
  ) {
    errors.push('Reason for Leaving is invalid');
  }

  let metaFields = enrolment.get('meta.fields');
  let activity = metaFields.get('nzMOE:postSchoolActivity.value');
  let area = metaFields.get('nzMOE:destinationArea.value');
  let school = metaFields.get('nzMOE:destinationSchool.value');
  switch (enrolment.departureReason) {
    case 'O':
    case 'X':
    case 'L':
    case 'E':
    case 'J':
      if (isEmpty(activity)) {
        errors.push('Post-School Activity is mandatory');
      } else if (
        isEmpty(findBy(PostSchoolActivity.values, 'value', activity))
      ) {
        errors.push('Post-School Activity is invalid');
      }
      break;

    case 'S':
    case 'H':
    case 'K':
    case 'C':
    case 'P':
      if (!isEmpty(area) && isEmpty(findBy(Area.values, 'value', area))) {
        errors.push('Destination Area is invalid');
      }
      if (!isEmpty(school) && isEmpty(findBy(School.values, 'value', school))) {
        errors.push('Destination School is invalid');
      }
      break;
  }

  if (isEmpty(enrolment.exitDate) || enrolment.exitDate <= '1970-01-01') {
    errors.push('Last day of attendance is not set');
  } else {
    let exitDate = moment(enrolment.exitDate, 'YYYY-MM-DD');
    let nextMonth = moment().add({ months: 1 });
    if (exitDate.isAfter(nextMonth, 'day')) {
      errors.push('Last day of attendance is more than 1 month in the future');
    }

    let dob = moment(person.dob, 'YYYY-MM-DD');
    switch (enrolment.departureReason) {
      case 'X':
      case 'L':
        // MoE spec: Last day of attendance must be > (DOB + 15 years + 10 months)
        if (
          exitDate.isSameOrBefore(
            moment(dob).add({ years: 15, months: 10 }),
            'day',
          )
        ) {
          errors.push(
            'Student must be at least 16 years old for this leaving reason',
          );
        }
        break;

      case 'E':
        // MoE spec: (DOB + 14 years + 10 months) < Last day of attendance > DOB + 16 years
        // Presumably, they mean LDoA < DOB + 16 years, or the first bit is redundant.
        if (
          !exitDate.isBetween(
            moment(dob).add({ years: 14, months: 10 }),
            moment(dob).add({ years: 16 }),
            'day',
          )
        ) {
          errors.push(
            'Student must be between 15 and 16 years old for this leaving reason',
          );
        }
        break;

      case 'K':
        // MoE spec: Last day of attendance must be < DOB + 16 years
        if (exitDate.isSameOrAfter(moment(dob).add({ years: 16 }), 'day')) {
          errors.push(
            'Student must be younger than 16 years old for this leaving reason',
          );
        }
        break;
    }
  }

  return errors;
}

export function generateLeaverRequestParams(person, enrolment, session) {
  let fields = person.get('fields');
  let metaFields = enrolment.get('meta.fields');
  let nsn = conformJurisdictionalId(
    Jurisdiction.NZ_MOE,
    person.jurisdictionalId,
  );
  let flags = metaFields.get('nzMOE:learnerFlags.value') ?? [];
  if (!isArray(flags)) {
    flags = [];
  }
  let nextSchools = [];
  let nextSchool = metaFields.get('nzMOE:destinationSchool.value');
  if (!isEmpty(nextSchool) && !(nextSchool === '998' || nextSchool === '999')) {
    nextSchools.push(Number(nextSchool));
  }
  let yearLevel = person.get('currentYearLevel') ?? 0;
  if (yearLevel === 0) {
    // We should only be generating params for already-validated learners,
    // so if we get a Year 0, that should be allowed for this school.
    // Turn them into a Year 1 for the upload, as ENROL doesn't allow Year 0.
    yearLevel = 1;
  }
  return {
    // Request params.
    requesterUsername: session.user.username,
    providerId: session.school.jurisdictionalId,
    personId: person.id,
    personEnrolmentId: enrolment.id,

    // Core identity data.
    nsn,
    birthDate: fields.get('core:dob.value'),
    gender: fields.get('nzMOE:gender.value'),

    // Required leaving data.
    lastAttendanceDate: enrolment.exitDate,
    yearLevel,
    reason: enrolment.departureReason,
    academicFlag: flags.includes('academic'),
    attendanceFlag: flags.includes('attendance'),
    behaviouralFlag: flags.includes('behavioural'),
    custodialFlag: flags.includes('custodial'),
    healthFlag: flags.includes('health'),
    personalFlag: flags.includes('personal'),

    // Sometimes-required leaving data.
    postSchoolActivity: metaFields.get('nzMOE:postSchoolActivity.value'),

    // Optional leaving data.
    nextArea: metaFields.get('nzMOE:destinationArea.value'),
    nextSchools,
  };
}
